import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { MEDIA, BRAND } from "@/lib/content";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Projects", href: "#projects" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const IT_PRODUCTS = [
  {
    name: "Scanvio",
    href: "https://scanvio.app",
    description: "Document scanning & automation",
  },
  {
    name: "CosmicSage",
    href: "https://cosomicsage.com/",
    description: "Astrology & spiritual intelligence platform",
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-background/70 border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="container-outer flex items-center justify-between h-16 md:h-20">
        <a
          href="#home"
          data-testid="brand-logo-link"
          aria-label={BRAND.name}
          className="flex items-center gap-3 group"
        >
          <img
            src={MEDIA.logo}
            alt={BRAND.short_name}
            className="h-10 md:h-11 w-auto object-contain"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading text-base md:text-lg font-semibold tracking-tight text-accent">
              {BRAND.short_name}
            </span>
            <span className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
              Consultancy & Electric Solutions Pvt Ltd
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="text-sm font-medium text-foreground/80 hover:text-accent transition-colors"
            >
              {item.label}
            </a>
          ))}

          {/* IT Products dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-it-products-trigger"
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-accent transition-colors focus:outline-none"
            >
              IT Products
              <ChevronDown size={14} strokeWidth={2} className="opacity-70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              className="w-64 border-border"
            >
              {IT_PRODUCTS.map((p) => (
                <DropdownMenuItem
                  key={p.name}
                  asChild
                  data-testid={`nav-it-${p.name.toLowerCase()}`}
                  className="p-3 cursor-pointer focus:bg-secondary"
                >
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 w-full"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-base font-semibold text-primary">
                          {p.name}
                        </span>
                        <ArrowUpRight
                          size={14}
                          className="text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {p.description}
                      </p>
                    </div>
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden md:block">
          <Button
            asChild
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-6"
            data-testid="header-cta-button"
          >
            <a href="#contact">Get a Quote</a>
          </Button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 rounded-md hover:bg-secondary"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="container-outer py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium py-2 text-foreground hover:text-accent"
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <div className="pt-2 border-t border-border/50">
              <p className="text-xs tracking-[0.18em] uppercase text-muted-foreground mb-2">
                IT Products
              </p>
              {IT_PRODUCTS.map((p) => (
                <a
                  key={p.name}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 py-2 text-base font-medium text-foreground hover:text-accent"
                  data-testid={`mobile-nav-it-${p.name.toLowerCase()}`}
                >
                  {p.name}
                  <ArrowUpRight size={14} className="text-accent" />
                </a>
              ))}
            </div>
            <Button
              asChild
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mt-2"
              data-testid="mobile-header-cta"
            >
              <a href="#contact" onClick={() => setOpen(false)}>
                Get a Quote
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
